import careStore from './careStore';
import checkStore from './checkStore';
import historyStore from './historyStore';
import hraStore from './hraStores';
import reservationStore from './reservationStore';
import stressStore from './stressStores';
import navStore from './useStore';

/**
 * 로그아웃 시 메모리에 남은 계정 데이터를 지운다.
 *
 * 왜 필요한가:
 * `util.clearLoginStorage()` 는 localStorage 만 비운다. 그런데 zustand `persist` 는
 * 앱이 처음 로드될 때 한 번만 localStorage 를 읽어 메모리 상태를 채우고, 그 뒤로는
 * 메모리 쪽이 원본이다. 이 앱은 로그아웃·로그인이 전부 soft navigation(`navigate()`)이라
 * 페이지가 다시 로드되지 않으므로, localStorage 를 비워도 스토어 7개는 **직전 계정의
 * 데이터를 그대로 들고 있다.**
 *
 * 결과가 두 가지다.
 *  1) 다른 계정으로 로그인해도 이전 계정의 예약 정보·건강 정보가 화면에 그대로 보인다.
 *     (뒤로/앞으로 가기처럼 재조회 없이 스토어에서 바로 그리는 경로에서 특히 잘 드러난다)
 *  2) 새 계정에서 아무 값이나 한 번 바꾸면 persist 가 **이전 계정 데이터를 통째로
 *     localStorage 에 다시 써넣는다.** 지운 데이터가 되살아난다.
 *
 * 그래서 저장소를 비우는 지점마다 이 함수를 함께 불러 메모리도 같이 비운다.
 *
 * setState 는 얕은 병합이라 데이터 키만 넘기면 된다 — 세터 함수들은 그대로 유지된다.
 *
 * 주의: 여기 빠진 상태 키가 생기면 그 값만 계정 사이를 넘어간다.
 * `resetStores.test.ts` 가 스토어의 모든 비함수 키를 열거해 누락을 잡는다.
 */

// 로그아웃 후에도 유지할 값. util.clearLoginStorage 의 KEEP_STORE_KEYS_ON_LOGOUT 과
// 반드시 같은 목록이어야 한다 — 한쪽만 유지하면 localStorage 와 메모리가 어긋난다.
//
// selectedDeviceOwner 를 함께 남기는 이유는 util.ts 의 같은 목록 주석 참고.
// 소유자 없이 목록만 남기면 다른 계정에 이전 계정의 기기 선택이 그대로 보인다.
const keepHistoryKeys = ['selectedDeviceList', 'selectedDeviceOwner'];

const emptyPlanInfo = () => ({
  title: '', target: 0, kcal: 0, unitType: '', infoText: '', navi: () => {},
});

export const resetAllStores = () => {
  careStore.setState({
    careInfo: {
      height: 0, weight: 0, bmi: 0, interest_disease: '', work_strength: '',
      goal: 0, food: 0, reset: '',
    },
  } as any);

  checkStore.setState({ selectedAnswerList: [] } as any);

  historyStore.setState({
    exerciseInfo: emptyPlanInfo(),
    mealInfo: emptyPlanInfo(),
    stepInfo: emptyPlanInfo(),
    moistureInfo: emptyPlanInfo(),
    selectedFoodList: [],
    selectedExerciseList: [],
    // selectedDeviceList / selectedDeviceOwner 는 의도적으로 건드리지 않는다
    // (keepHistoryKeys 참고 — 소유자가 다르면 읽는 쪽에서 걸러진다)
  } as any);

  hraStore.setState({
    // cusId 는 여기서 localStorage 를 다시 읽지 않는다. 이 함수는 저장소를 비우기
    // 전후 어느 쪽에서 불릴지 보장되지 않으므로, 비워두고 다음 로그인이 채우게 한다.
    hraListParams: { cusId: null, pageNum: 0 },
    hraList: [],
    hraListCount: 0,
    targetInfo: [],
    targetSurvey: [],
    questionsList: [],
    assignedUid: '',
    splitableTitleList: [],
    selected: '-1',
    age: 0,
    checked: 0,
    viewObject: {},
    factorsOject: {},
    familyOject: {},
    integrateOject: {},
    resultOject: {},
  } as any);

  reservationStore.setState({
    reservationInfo: {
      hosId: '', addPrice: 0, date: '', time: '', companyId: '', pkgId: '',
      famId: '', groudPrice: 0,
    },
    checkedInjectioList: [],
    closeItems: [],
    choiceLimitCount: 0,
    pakageInfo: {
      piis: {},
      result: { id: '', name: '', serviceName: '', companyId: '', hospitalId: '', price: 0 },
    },
  } as any);

  stressStore.setState({
    stressListParams: { cusId: null, pageNum: 0 },
    stressList: [],
    stressListCount: 0,
    targetInfo: [],
    targetSurvey: [],
    questionsList: [],
    resultList: [],
    surveyAssigned: {},
    familyInfo: {},
    surveyInfoList: [],
  } as any);

  navStore.setState({ bottomNavValue: '' } as any);
};

/** 테스트에서 유지 대상 목록을 검증하기 위해 노출한다. */
export const KEEP_ON_LOGOUT = { historyStore: keepHistoryKeys };

export default resetAllStores;
